/**
 * LE PUPITRE — la lampe et la taille du texte, les deux réglages de la LECTURE (design §5).
 *
 * POURQUOI ICI ET PAS DANS UN ÉCRAN DE COMPTE. Ce sont des réglages qu'on prend en lisant : le
 * texte est trop petit MAINTENANT, la pièce est trop sombre MAINTENANT. Les enfouir dans un menu,
 * c'est demander de quitter le livre pour régler le livre — et, dans un jeu à hauteur d'écran
 * fixe, c'est aussi perdre sa page.
 *
 * LE RÉGLAGE SE VOIT PENDANT QU'ON LE FAIT : le spécimen de texte au-dessus des boutons est écrit
 * dans le corps courant, et il change sous les doigts. Un curseur de taille sans exemple oblige à
 * fermer, regarder, rouvrir, recommencer.
 *
 * DEUX BOUTONS PLUTÔT QU'UNE LISTE. On règle une taille en tâtonnant — un cran de plus, encore un,
 * ah non, un de moins —, pas en choisissant « 1,32 » dans un menu déroulant. Les cinq crans sont
 * quand même montrés, en points, pour savoir où l'on en est et qu'il y a une fin.
 */
import { Glyphe } from "../../composants/Glyphe.js";
import { CRANS, cranVoisin, useTailleDuTexte } from "../../lecture/taille.js";
import { useMainsLibres, useVitesse, VITESSES, vitesseVoisine } from "../../lecture/voix.js";
import { LANGUES, NOMS_DE_LANGUE, poseLaLangue, t, useLangue } from "../../lexique/langue.js";
import { useTheme } from "../../theme.js";

/** Le spécimen se relit à chaque rendu : il change avec la langue, comme le reste du pupitre. */
function specimen(): string {
  return t(
    "Le pont de corde tient encore, mais les planches manquent par endroits, et le vent qui monte de la gorge sent la pierre mouillée."
  );
}

export function LePupitre() {
  const laLangue = useLangue();
  const [theme, basculeLeTheme] = useTheme();
  const [cran, poseLeCran] = useTailleDuTexte();
  const [vitesse, poseLaVitesse] = useVitesse();
  const [mainsLibres, poseLesMainsLibres] = useMainsLibres();
  const rang = CRANS.findIndex((c) => c.clef === cran);
  const rangDeVitesse = VITESSES.findIndex((v) => v.clef === vitesse);

  return (
    <>
      {/* LA LANGUE EST UN RÉGLAGE DE LECTURE (§13.2), pas un réglage de compte : elle vit ici, avec
          la lampe et la taille du texte, et elle s'applique sur-le-champ — on ne recharge pas une
          page de roman. Chaque langue s'écrit DANS SA PROPRE LANGUE : c'est la seule façon qu'un
          lecteur reconnaisse la sienne dans une liste qu'il ne sait pas encore lire. */}
      <div className="tiroir__soustitre capitales">{t("La langue")}</div>
      <div className="pile pile--serree">
        {LANGUES.map((code) => (
          <button
            key={code}
            type="button"
            className={code === laLangue ? "bouton bouton--choisi" : "bouton"}
            aria-pressed={code === laLangue}
            onClick={() => poseLaLangue(code)}
          >
            {NOMS_DE_LANGUE[code]}
          </button>
        ))}
      </div>
      <p className="faible">
        {t("L'interface et le carnet de bord suivent votre langue. Une aventure, elle, reste dans celle où elle a été écrite.")}
      </p>

      <div className="tiroir__soustitre capitales">{t("La lampe")}</div>
      <div className="rang rang--espace">
        <span>{theme === "veillee" ? t("Veillée — lampe éteinte") : t("Parchemin — plein jour")}</span>
        <button type="button" className="bouton" onClick={basculeLeTheme}>
          <Glyphe nom="lampe" taille={15} />
          {theme === "veillee" ? t("Rallumer") : t("Éteindre")}
        </button>
      </div>
      <p className="faible">
        {t("La veillée est faite pour lire au lit, la lampe basse. Le parchemin est fait pour tout le reste.")}
      </p>

      <div className="tiroir__soustitre capitales">{t("La taille du texte")}</div>
      <p className="pupitre__specimen">{specimen()}</p>

      <div className="rang rang--espace">
        <button
          type="button"
          className="bouton"
          disabled={rang <= 0}
          onClick={() => poseLeCran(cranVoisin(cran, -1))}
          aria-label={t("Réduire le texte")}
        >
          <Glyphe nom="lettre" taille={13} /> {t("Plus petit")}
        </button>

        <span className="pupitre__crans" role="presentation">
          {CRANS.map((c) => (
            <span key={c.clef} className={c.clef === cran ? "pupitre__cran pupitre__cran--pose" : "pupitre__cran"} />
          ))}
        </span>

        <button
          type="button"
          className="bouton"
          disabled={rang >= CRANS.length - 1}
          onClick={() => poseLeCran(cranVoisin(cran, 1))}
          aria-label={t("Agrandir le texte")}
        >
          <Glyphe nom="lettre" taille={18} /> {t("Plus grand")}
        </button>
      </div>
      <p className="faible capitales" aria-live="polite" style={{ textAlign: "center" }}>
        {t(CRANS[rang]?.nom ?? "Normal")}
      </p>
      <p className="faible">
        {t("La colonne suit : elle garde le même nombre de caractères par ligne, et le découpage en feuillets se refait pour la nouvelle taille.")}
      </p>

      {/* LA VOIX (§7). Sa vitesse est un réglage de LECTURE, au même titre que la lampe et le corps
          du texte : on la règle en écoutant, pas dans un écran de compte. */}
      <div className="tiroir__soustitre capitales">{t("La voix")}</div>

      <div className="rang rang--espace">
        <button
          type="button"
          className="bouton"
          disabled={rangDeVitesse <= 0}
          onClick={() => poseLaVitesse(vitesseVoisine(vitesse, -1))}
          aria-label={t("Lire plus lentement")}
        >
          <Glyphe nom="revers" taille={13} /> {t("Plus lente")}
        </button>

        <span className="pupitre__crans" role="presentation">
          {VITESSES.map((v) => (
            <span key={v.clef} className={v.clef === vitesse ? "pupitre__cran pupitre__cran--pose" : "pupitre__cran"} />
          ))}
        </span>

        <button
          type="button"
          className="bouton"
          disabled={rangDeVitesse >= VITESSES.length - 1}
          onClick={() => poseLaVitesse(vitesseVoisine(vitesse, 1))}
          aria-label={t("Lire plus vite")}
        >
          {t("Plus vive")} <Glyphe nom="suite" taille={13} />
        </button>
      </div>
      <p className="faible capitales" aria-live="polite" style={{ textAlign: "center" }}>
        {t(VITESSES[rangDeVitesse]?.nom ?? "Normale")}
      </p>

      {/* LE MAINS LIBRES EST LA SEULE CHOSE DU JEU QUI TOURNE LES PAGES TOUTE SEULE. C'est
          l'exception à « rien ne s'ouvre tout seul », et elle s'arme ici, explicitement. */}
      <label className="rang rang--serre" style={{ marginTop: "var(--e3)", marginBottom: "var(--e2)" }}>
        <input type="checkbox" checked={mainsLibres} onChange={(e) => poseLesMainsLibres(e.target.checked)} />
        <span>
          {t("Mains libres")} <span className="faible">{t("— la voix tourne les feuillets et s'arrête devant les choix")}</span>
        </span>
      </label>
      <p className="faible" style={{ marginBottom: 0 }}>
        {t("Aucun choix ne se prend tout seul : la lecture s'arrête à l'amorce, et c'est à vous. Toucher la page coupe la voix.")}
      </p>
    </>
  );
}
